/**
 * The web app's passkey words and rules (docs/members.md "People and passkeys"), kept free of the
 * DOM, of Preact and of WebAuthn so Node's test runner can check them (tests/passkeys-ui.test.ts).
 * The ceremonies themselves are src/passkeys.ts.
 */
import { formatShortDate } from "./format.ts";

/** What the gate and the account sheet say when the phone's passkey prompt was closed or failed. */
export const PASSKEY_TRY_AGAIN = "That did not work. Try again.";

/** The one line under "Add a passkey". */
export const PASSKEY_PITCH = "Sign in on any phone as you. Stewards: a lost phone is not a lost household.";

/** Under a steward in the House screen who holds no passkey. */
export const NO_PASSKEY_LINE = "No passkey yet.";

/**
 * The words for a failed passkey attempt: the server's own words when it answered (an ApiError
 * carries a numeric status), else "That did not work. Try again." (the prompt was closed, timed
 * out, or the phone said no; the browser calls all of those AbortError or NotAllowedError).
 */
export function passkeyErrorMessage(e: unknown): string {
  // Status 0 is no connection: its message already says so.
  return serverAnswered(e) ? (e as Error).message : PASSKEY_TRY_AGAIN;
}

/** Whether a failure is an answer from the server (an ApiError, with its words), not the browser's prompt. */
export function serverAnswered(e: unknown): boolean {
  return e instanceof Error && typeof (e as { status?: unknown }).status === "number" && e.message.length > 0;
}

/** Whether a failure is the conditional (autofill) request being called off, which says nothing to anyone. */
export function isQuietAbort(e: unknown): boolean {
  if (!(e instanceof Error)) return false;
  const code = (e as { code?: unknown }).code;
  return e.name === "AbortError" || code === "ERROR_CEREMONY_ABORTED";
}

/**
 * Whether the gate offers "Sign in with a passkey": the workshop's `passkeys` switch is on for this
 * device, the server says this host can use passkeys, and the browser has WebAuthn.
 */
export function gateOffersPasskey(available: boolean | undefined, browserHasWebAuthn: boolean): boolean {
  return available === true && browserHasWebAuthn;
}

/** A passkey's line in the account sheet: "iPhone · Added Sep 30". */
export function passkeyRowText(p: { label: string; created_at: string }): string {
  return `${p.label || "Passkey"} · Added ${formatShortDate(p.created_at)}`;
}

/** The House screen's badge and nudge for one device: the word "passkey" when its person holds one, the nudge for a steward who does not. */
export function memberPasskeyMarks(m: { role: string; person?: { passkeys: number } | null }): { badge: boolean; nudge: boolean } {
  const held = (m.person?.passkeys ?? 0) > 0;
  return { badge: held, nudge: m.role === "steward" && !held };
}
